<template>
  <div class="dea-modal-overlay" :class="visibleClass">
    <div :class="['dea-modal', extraClass, { 'dea-modal--full-width': hasFullWidth }]">
      <div class="dea-modal__title-container">
        <div class="dea-modal__title">
          <h2 class="dea-modal__title-text" data-test="modal__title">
            <div v-if="isError" class="dea-modal--action__error-icon dea-ico-error"></div>
            <slot name="title"></slot>
          </h2>
        </div>
        <div class="dea-modal__close-button-container">
          <button
            class="dea-modal__close-button dea-ico-close"
            :class="{ 'dea-modal__close-button--light': hasLightBtn }"
            data-test="modal__button-close"
            @click.stop="
              $emit('modalClose');
              $emit('modal-close');
            "
          ></button>
        </div>
      </div>
      <div class="dea-modal__content">
        <slot name="content"></slot>
      </div>
      <div class="dea-modal__footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Modal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    isError: {
      type: Boolean,
      default: false,
    },
    hasFullWidth: {
      type: Boolean,
      default: false,
    },
    hasLightBtn: {
      type: Boolean,
      default: false,
    },
    extraClass: {
      type: String,
      default: "",
    },
  },
  computed: {
    visibleClass() {
      return this.visible ? "dea-modal-overlay--visible" : "";
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/system.utils"; //sass-lint:disable-line clean-import-paths

$modal-padding: $space-large;

.dea-modal {
  @include bp-large-max {
    padding: $space-base;
  }
  background: $dea-color-white;
  margin: 0 auto;
  position: relative;
  top: 50%;
  transform: translateY(-50%);

  &--share {
    @include media("<s") {
      width: 100%;
    }
    padding: 0;
    width: 50%;

    .dea-modal__title-container {
      margin-bottom: 0;
    }
  }

  &--shadow {
    .dea-modal__title-container {
      box-shadow: $box-shadow-extra-strong;
    }
  }

  &--mobile {
    padding: $space-base;

    .dea-modal__title-text {
      @include h-font-size-large;
      margin-right: 0;
    }

    .dea-modal__text {
      margin-bottom: $space-base;
      margin-right: 0;
    }

    .dea-modal__button {
      &--desktop {
        display: none;
      }

      &--mobile {
        display: block;
      }
    }

    .dea-modal__close-button {
      height: $dea-button-size-small;
      width: $dea-button-size-small;
    }
  }

  &--attivazione--mobile {
    .dea-modal__input-container--attivazione {
      flex-direction: column;
      margin-top: $space-base;
    }

    .dea-modal__button--attivazione {
      margin-bottom: $space-x-small;
      margin-right: 0;
    }
  }

  &--action--mobile {
    .dea-modal--action__error-icon {
      height: $dea-button-size-small;
      width: $dea-button-size-small;
    }
  }

  &--full-width {
    height: 100%;
    left: 0 !important; //sass-lint:disable-line no-important
    max-width: none;
    top: 0 !important; //sass-lint:disable-line no-important
    transform: translate(0, 0) !important; //sass-lint:disable-line no-important
    width: 100%;
  }

  .dea-modal__title-container {
    box-shadow: none;
    max-width: none;
  }

  &-overlay {
    display: none;

    &--visible {
      background-color: rgba($dea-color-black, 0.8);
      bottom: 0;
      display: block !important; // sass-lint:disable-line no-important
      left: 0;
      position: fixed;
      right: 0;
      top: 0;
      z-index: 999999;
    }

    &--hidden {
      display: none;
    }

    &--paypal {
      display: none;

      &.active {
        display: block;
      }
    }

    > .dea-modal {
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &-buttons {
    display: flex;
    justify-content: space-between;
    margin: $space-base 0;
  }

  &__close-button--light {
    background-color: transparent;
    border: $border-light $dea-color-grey-dark;
  }

  &-section {
    flex-grow: 1;
    padding: 0 $space-small;

    &__image {
      max-width: 100%;
    }
  }

  &__title {
    display: flex;
    flex-direction: column;
    justify-content: center;

    &--attivazione {
      margin-bottom: $space-small;
      padding-top: $space-xx-small;
    }

    &-text {
      @include h-font-size-xx-large;

      @include bp-large-max {
        @include h-font-size-large;
        margin-right: 0;
      }
      align-items: center;
      display: flex;
      font-family: $font-primary-medium;
      margin-bottom: 0;
      margin-right: $space-x-large;
      margin-top: 0;

      &--attivazione {
        @include h-font-size-large;
      }

      .dea-modal--action__error-icon {
        margin-bottom: 0;
        margin-right: $space-small;
      }
    }

    &-container {
      @include media("<m") {
        padding: $space-base;
      }

      align-items: stretch;
      display: flex;
      justify-content: space-between;
      margin-bottom: $space-small;
      padding: $modal-padding;

      &--attivazione {
        align-items: flex-start;
        margin-bottom: 0;
      }
    }
  }

  &__text {
    @include font-size-small;
    @include bp-large-max {
      margin-right: 0;
    }
    font-family: $font-primary;
    margin-right: $space-x-large;
    margin-top: 0;
  }

  &__content {
    @include media("<m") {
      padding: 0 $space-base;
    }

    padding: 0 $modal-padding;
  }

  &__actions {
    margin-bottom: $space-base;

    > * + * {
      margin-left: $space-base;
    }
  }

  &__button {
    &--desktop {
      @include bp-large-max {
        display: none;
      }
    }

    &--mobile {
      @include bp-large-max {
        display: block;
      }
      display: none;
    }

    &--attivazione {
      @include bp-large-max {
        margin-bottom: $space-x-small;
        margin-right: 0;
      }
      margin-right: $space-small;
    }

    &--modal-input {
      margin-top: $space-base;
      max-width: 360px;
      width: 100%;
    }
  }

  &__input-container--attivazione {
    @include bp-large-max {
      flex-direction: column;
      margin-top: $space-base;

      & > * {
        margin: $space-small 0;
      }
    }
    display: flex;
    margin-top: $space-large;
  }

  &--modal-input__text-field {
    max-width: 360px;
    width: 100%;
  }

  &__close-button {
    @include bp-large-max {
      height: $dea-button-size-small;
      width: $dea-button-size-small;
    }
    align-items: center;
    border: $border-bold $dea-color-grey-dark;
    border-radius: $border-radius-circle;
    display: flex;
    height: $dea-size-large;
    justify-content: center;
    padding: 0;
    width: $dea-size-large;

    &:before {
      font-size: $dea-size-medium;
    }
  }

  &__footer {
    background-color: $dea-color-grey-light;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    padding: $space-base $modal-padding;

    > * {
      margin: 0 $space-base;

      &:first-of-type {
        margin-left: 0;
      }

      &:last-of-type {
        margin-right: 0;
      }
    }
  }

  &--title-no-spacing {
    .dea-modal__title-container {
      margin-bottom: 0;
      padding-bottom: 0;
    }
  }

  &--no-footer {
    .dea-modal__footer {
      display: none;
    }
  }

  .share-actions {
    display: flex;
    margin-top: $space-small;
    width: 100%;

    .dea-social-logo + .dea-social-logo {
      margin-left: $space-x-small;
    }
  }

  .spacer {
    flex: 1;
  }

  iframe {
    border: 0;
    display: block;
    width: 100%;
  }
}
</style>
